import { Form } from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useGetAnalyticsServiceCategoriesMutation,
  useLazyGetAnalyticsFilterDataQuery,
} from '@app/api/Analytics';
import {
  DATE_PICKER_FORMAT,
  fistDayOfCurrentMonth,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  RIGHTS_ANALYTICS,
  RIGHTS_IS_ON,
  today,
  WARNING,
} from '@app/constants';
import { useAccessRights, useUserPrint } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { WidthArchiveLabel } from '@app/shared/components';
import { useRegistryFilterState } from '@app/shared/hooks';
import type { NAnalytics } from '@app/types/Analytics';
import { addEnterKeyListener, handleDate } from '@app/utils';

import { FINALLY } from '@pages/Analytics/DirectionsIndicatorsList/constants/categories';

import type { BaseOptionType } from 'antd/es/select';
import type { Dayjs } from 'dayjs';

type GetSelectOptionsData =
  | {
      label: string;
      value: string | number;
      filterName?: string | null;
      IsDeleted?: boolean;
    }[]
  | null;

export const useDirectionIndicators = () => {
  const { activeBranchId } = useUserPrint();
  const { hasUserRightForModuleView } = useAccessRights(
    RIGHTS_ANALYTICS,
    RIGHTS_IS_ON
  );

  const [filterData, setFilterData] =
    useState<NAnalytics.ModelAnalyticsFilterData>();
  const [searchError, setSearchError] = useState('');
  // Выбранные фильтры отчёта переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161): хранилище вкладочное, как у реестра
  // записей и реестра задач. Ключ поля включает имя отчёта, чтобы отчёты
  // аналитики не перетирали фильтры друг друга.
  const [selectedEmployees, setSelectedEmployees] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'directions:selectedEmployees', null);
  const [selectedUsers, setSelectedUsers] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'directions:selectedUsers', null);
  const [selectedClientServices, setSelectedClientServices] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'analytics',
      'directions:selectedClientServices',
      null
    );
  const [selectedClientServiceCategories, setSelectedClientServiceCategories] =
    useRegistryFilterState<BaseOptionType[] | null>(
      'analytics',
      'directions:selectedClientServiceCategories',
      null
    );
  // Первая загрузка таблицы учитывает фильтры, восстановленные из хранилища
  // (issue/11161). Через ref, чтобы смена фильтра не перезапрашивала таблицу
  // до нажатия «Применить».
  const selectedFiltersRef = useRef({
    selectedEmployees,
    selectedUsers,
    selectedClientServices,
    selectedClientServiceCategories,
  });
  selectedFiltersRef.current = {
    selectedEmployees,
    selectedUsers,
    selectedClientServices,
    selectedClientServiceCategories,
  };
  const [analyticsServiceCategories, setAnalyticsServiceCategories] =
    useState<NAnalytics.ModelAnalyticsServiceCategories>();

  const { showNotification } = useContext(NotificationContext);

  const [getAnalyticsFilterData, { isFetching: isFiltersLoading }] =
    useLazyGetAnalyticsFilterDataQuery();

  const [
    getAnalyticsServiceCategories,
    { isLoading: isGetAnalyticsServiceCategoriesLoading },
  ] = useGetAnalyticsServiceCategoriesMutation();

  const [form] = Form.useForm();

  const setTableFooter = useCallback(
    ({
      Categories,
      TotalPrice,
      TotalSalePlans,
      TotalFirstClients,
      TotalSecondClient,
      TotalAdditionalSales,
      ...rest
    }: NAnalytics.ModelAnalyticsServiceCategories) => {
      if (!Categories)
        return showNotification(
          'Данные для отображения шапки таблицы не найдены',
          FRONT_WARNING
        );

      const extendedCategories = [
        ...Categories,
        {
          CategoryId: 0,
          CategoryName: FINALLY,
          Price: TotalPrice,
          PercentagePrice: 100,
          SalePlans: TotalSalePlans,
          FirstClients: TotalFirstClients,
          SecondClients: TotalSecondClient,
          AdditionalSales: TotalAdditionalSales,
        },
      ];

      setAnalyticsServiceCategories({
        Categories: extendedCategories,
        TotalPrice,
        TotalSalePlans,
        TotalFirstClients,
        TotalSecondClient,
        TotalAdditionalSales,
        ...rest,
      });
    },
    [showNotification]
  );

  useEffect(() => {
    const fetchAnalyticsServiceCategories = async () => {
      try {
        const {
          selectedEmployees: employees,
          selectedUsers: users,
          selectedClientServices: clientServices,
          selectedClientServiceCategories: clientServiceCategories,
        } = selectedFiltersRef.current;
        const getIds = (options: BaseOptionType[] | null) =>
          options?.length ? options.map(({ value }) => value) : null;

        const result = await getAnalyticsServiceCategories({
          ActiveBranchId: activeBranchId,
          data: {
            DateStart: handleDate(fistDayOfCurrentMonth),
            DateEnd: handleDate(today),
            EmployeeIds: getIds(employees),
            UserIds: getIds(users),
            ServiceIds: getIds(clientServices),
            CategoryIds: getIds(clientServiceCategories),
          },
        }).unwrap();

        setTableFooter(result);
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    const fetchAnalyticsFilterData = async () => {
      try {
        const result = await getAnalyticsFilterData({
          ActiveBranchId: activeBranchId,
        }).unwrap();

        setFilterData(result);

        fetchAnalyticsServiceCategories();
      } catch (error) {
        showNotification(handleCatchError(error), WARNING);
      }
    };

    fetchAnalyticsFilterData();

    return addEnterKeyListener({
      callback: form.submit,
    });
  }, [
    activeBranchId,
    form.submit,
    showNotification,
    setTableFooter,
    getAnalyticsServiceCategories,
    getAnalyticsFilterData,
  ]);

  const handleReset = async () => {
    try {
      setSelectedEmployees([]);
      setSelectedUsers([]);
      setSelectedClientServices([]);
      setSelectedClientServiceCategories([]);
      form.setFieldValue('Date', [fistDayOfCurrentMonth, today]);

      const result = await getAnalyticsServiceCategories({
        ActiveBranchId: activeBranchId,
        data: {
          DateStart: handleDate(fistDayOfCurrentMonth),
          DateEnd: handleDate(today),
          EmployeeIds: null,
          UserIds: null,
          ServiceIds: null,
          CategoryIds: null,
        },
      }).unwrap();

      setTableFooter(result);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  };

  const submitForm = async (values: { Date: [Dayjs, Dayjs] }) => {
    try {
      const DateStart = values.Date?.[0]
        ? values.Date[0].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null;
      const DateEnd = values.Date?.[1]
        ? values.Date[1].format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
        : null;

      if (!DateStart || !DateEnd)
        return showNotification(
          'Пожалуйста, заполните обе даты периода в фильтрах',
          FRONT_WARNING
        );

      const UserIds = selectedUsers?.length
        ? selectedUsers.map((item) => item.value)
        : null;
      const EmployeeIds = selectedEmployees?.length
        ? selectedEmployees.map((item) => item.value)
        : null;
      const ServiceIds = selectedClientServices?.length
        ? selectedClientServices.map((item) => item.value)
        : null;
      const CategoryIds = selectedClientServiceCategories?.length
        ? selectedClientServiceCategories.map((item) => item.value)
        : null;

      const result = await getAnalyticsServiceCategories({
        ActiveBranchId: activeBranchId,
        data: {
          DateStart,
          DateEnd,
          EmployeeIds,
          UserIds,
          ServiceIds,
          CategoryIds,
        },
      }).unwrap();

      setTableFooter(result);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  };

  const getSelectOptions = useCallback((data: GetSelectOptionsData) => {
    if (!data?.length) return [];

    return data
      .sort((a, b) => {
        const isADeleted = a.IsDeleted || false;
        const isBDeleted = b.IsDeleted || false;

        if (isADeleted !== isBDeleted) {
          return isADeleted ? 1 : -1;
        }
        return a?.label?.localeCompare(b?.label ?? '') || 0;
      })
      .map(({ label, filterName, IsDeleted, value }) => ({
        label: <WidthArchiveLabel title={label} isDeleted={IsDeleted} />,
        value: value,
        filterName: filterName,
      }));
  }, []);

  const getEmployeesSelectOptions = useCallback(() => {
    if (!filterData?.Employees?.length) return [];
    const { Employees } = filterData;

    const data = Employees.map((employee) => {
      const label = `${employee?.NameLast} ${employee?.NameFirst}`?.trim();

      return {
        label,
        value: employee?.Id,
        filterName: label,
        IsDeleted: employee?.IsDeleted,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const getUserSelectOptions = useCallback(() => {
    if (!filterData?.Users?.length) return [];
    const { Users } = filterData;

    const data = Users.map((user) => {
      const label = `${user?.NameLast || ''} ${user?.NameFirst || ''}`?.trim();
      return {
        label,
        value: user.Id || 0,
        filterName: label || '',
        IsDeleted: user.IsDeleted || false,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const getClientServiceSelectOptions = useCallback(() => {
    if (!filterData?.ClientServices) return [];

    const { ClientServices } = filterData;

    const data: GetSelectOptionsData = ClientServices.map((service) => {
      const label = (service?.Name || '')?.trim();
      return {
        label,
        value: service?.Id,
        filterName: label,
        IsDeleted: service?.IsDeleted,
      };
    });

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const getClientServiceCategoriesSelectOptions = useCallback(() => {
    if (!filterData?.ClientServiceCategories) return [];

    const { ClientServiceCategories } = filterData;

    const data: GetSelectOptionsData = ClientServiceCategories.map(
      (category) => {
        const label = (category?.Name || '')?.trim();
        return {
          label,
          value: category?.Id,
          filterName: label,
          IsDeleted: category?.IsDeleted,
        };
      }
    );

    return getSelectOptions(data);
  }, [filterData, getSelectOptions]);

  const employeesSelectOptions = useMemo(getEmployeesSelectOptions, [
    getEmployeesSelectOptions,
  ]);

  const userSelectOptions = useMemo(getUserSelectOptions, [
    getUserSelectOptions,
  ]);

  const clientServiceCategoriesSelectOptions = useMemo(
    getClientServiceCategoriesSelectOptions,
    [getClientServiceCategoriesSelectOptions]
  );

  const clientServiceSelectOptions = useMemo(getClientServiceSelectOptions, [
    getClientServiceSelectOptions,
  ]);

  return {
    isFiltersLoading,
    form,
    today,
    searchError,
    setSearchError,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedClientServices,
    setSelectedClientServices,
    selectedClientServiceCategories,
    setSelectedClientServiceCategories,
    analyticsServiceCategories,
    handleReset,
    submitForm,
    hasUserRightForModuleView,
    isGetAnalyticsServiceCategoriesLoading,
    employeesSelectOptions,
    userSelectOptions,
    clientServiceCategoriesSelectOptions,
    clientServiceSelectOptions,
  };
};
